import { useEffect, useState } from "react";
import { PageHeader, EmptyState, Pill } from "@/components/ui-bits";
import { Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useConstants } from "@/lib/constants";

export default function UsersAdmin({ role, title, subtitle, testIdPrefix }) {
  const constants = useConstants();
  const [users, setUsers] = useState([]);
  const [batches, setBatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", employee_id: "", batch_id: "", designation: "" });
  const [error, setError] = useState("");

  const load = () => {
    api.get(`/users?role=${role}`).then(r => setUsers(r.data));
    if (role === "student") api.get("/batches").then(r => setBatches(r.data));
  };
  useEffect(load, [role]);

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const created = await api.post("/users", {
        name: form.name,
        email: form.email,
        password: "Solemnity@123",
        role,
        employee_id: form.employee_id?.trim() || null,
        batch_id: role === "student" ? (form.batch_id || null) : null,
      });
      // If trainer designation chosen, patch immediately.
      if (role === "trainer" && form.designation) {
        await api.patch(`/users/${created.data.id}`, { designation: form.designation });
      }
      setForm({ name: "", email: "", employee_id: "", batch_id: "", designation: "" });
      setOpen(false);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create user");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    await api.delete(`/users/${id}`);
    load();
  };

  return (
    <div>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          <button
            data-testid={`${testIdPrefix}-add-button`}
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 bg-blue-600 text-white rounded-lg px-4 py-2 font-medium hover:bg-blue-700">
            <Plus className="w-4 h-4" /> Add {role}
          </button>
        }
      />

      {open && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
          <form onSubmit={create} className="grid md:grid-cols-2 gap-4">
            <Field label="Full name">
              <input required value={form.name} onChange={e=>setForm({...form, name: e.target.value})} className={input} data-testid={`${testIdPrefix}-name-input`} />
            </Field>
            <Field label="Email">
              <input required type="email" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} className={input} data-testid={`${testIdPrefix}-email-input`} />
            </Field>
            <Field label="Employee ID (optional)">
              <input
                value={form.employee_id}
                onChange={e=>setForm({...form, employee_id: e.target.value.toUpperCase()})}
                placeholder="Leave blank to auto-generate"
                pattern="[A-Za-z0-9_\-]{3,32}"
                title="3–32 alphanumeric characters (dash/underscore allowed)"
                className={input}
                data-testid={`${testIdPrefix}-empid-input`}
              />
            </Field>
            {role === "trainer" && (
              <Field label="Designation (optional)">
                <select
                  value={form.designation}
                  onChange={e=>setForm({...form, designation: e.target.value})}
                  className={input}
                  data-testid={`${testIdPrefix}-designation-select`}
                >
                  <option value="">— None —</option>
                  {(constants.trainer_designations || []).map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </Field>
            )}
            {role === "student" && (
              <Field label="Batch">
                <select value={form.batch_id} onChange={e=>setForm({...form, batch_id: e.target.value})} className={input}>
                  <option value="">— None —</option>
                  {batches.map(b => <option key={b.id} value={b.id}>{b.batch_name}</option>)}
                </select>
              </Field>
            )}
            <div className="md:col-span-2 text-xs text-slate-500 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              The new account will receive the default password <code className="font-mono-acc text-blue-900">Solemnity@123</code>. The user must change it on first login.
            </div>
            {error && <div className="md:col-span-2 text-sm text-red-600">{error}</div>}
            <div className="md:col-span-2 flex justify-end gap-2">
              <button type="button" onClick={()=>setOpen(false)} className="px-4 py-2 border border-slate-300 rounded-lg text-sm">Cancel</button>
              <button data-testid={`${testIdPrefix}-create-submit`} type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm">Create</button>
            </div>
          </form>
        </div>
      )}

      {users.length === 0 ? (
        <EmptyState title={`No ${role}s yet`} hint={`Add your first ${role}.`} />
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-6 py-3 font-medium">Name</th>
                <th className="px-6 py-3 font-medium">Employee ID</th>
                <th className="px-6 py-3 font-medium">Email</th>
                {role === "trainer" && <th className="px-6 py-3 font-medium">Designation</th>}
                {role === "student" && <th className="px-6 py-3 font-medium">Batch</th>}
                <th className="px-6 py-3 font-medium">Joined</th>
                <th className="px-6 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const batch = batches.find(b => b.id === u.batch_id);
                return (
                  <tr key={u.id} className="border-t border-slate-100" data-testid={`${testIdPrefix}-row-${u.id}`}>
                    <td className="px-6 py-3 font-medium text-slate-900">{u.name}</td>
                    <td className="px-6 py-3 text-slate-600">
                      {u.employee_id
                        ? <span className="font-mono-acc text-xs px-2 py-0.5 bg-slate-100 text-slate-700 rounded">{u.employee_id}</span>
                        : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-6 py-3 text-slate-600">{u.email}</td>
                    {role === "trainer" && (
                      <td className="px-6 py-3">
                        {u.designation
                          ? <Pill tone="blue">{u.designation}</Pill>
                          : <span className="text-slate-400">—</span>}
                      </td>
                    )}
                    {role === "student" && <td className="px-6 py-3 text-slate-600">{batch?.batch_name || "—"}</td>}
                    <td className="px-6 py-3 text-slate-500">{u.created_at?.slice(0,10) || "—"}</td>
                    <td className="px-6 py-3 text-right">
                      <button data-testid={`${testIdPrefix}-delete-${u.id}`} onClick={()=>del(u.id)} className="text-red-600 hover:bg-red-50 rounded-lg p-2">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const input = "w-full border border-slate-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-slate-900 bg-white";
function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>{children}</div>;
}
